import React, { useState } from 'react';
import { suggestActivity } from '../../services/plansApi';
import './ForkModal.css';

interface SuggestModalProps {
  planId: number;
  creatorName: string;
  isAuthed: boolean;
  onClose: () => void;
  onSignUp: () => void;
}

const SuggestModal: React.FC<SuggestModalProps> = ({
  planId,
  creatorName,
  isAuthed,
  onClose,
  onSignUp,
}) => {
  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [note, setNote] = useState('');
  const [timePref, setTimePref] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!isAuthed) {
    return (
      <div className="fork-modal-backdrop" onClick={onClose}>
        <div className="fork-modal" onClick={e => e.stopPropagation()}>
          <div className="fork-modal-handle" />
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div className="fork-modal-icon" style={{ margin: '0 auto 12px' }}>
              <span style={{ fontSize: 20 }}>💡</span>
            </div>
            <h3 className="fork-modal-title">Suggest an activity</h3>
            <p className="fork-modal-desc">
              Sign up to suggest activities to {creatorName}. It's free.
            </p>
          </div>
          <button className="fork-modal-primary-btn" onClick={onSignUp}>
            Sign up to suggest
          </button>
          <button className="fork-modal-dismiss" onClick={onClose}>
            Just browsing
          </button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="fork-modal-backdrop" onClick={onClose}>
        <div className="fork-modal" onClick={e => e.stopPropagation()}>
          <div className="fork-modal-handle" />
          <div className="fork-modal-success">
            <div className="fork-modal-success-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-sage)" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
            </div>
            <h3 className="fork-modal-title">Suggestion sent!</h3>
            <p className="fork-modal-subtitle">
              {creatorName} will see your suggestion and can add it to the plan.
            </p>
            <button className="fork-modal-primary-btn" style={{ background: 'var(--text-primary)', marginTop: 16 }} onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async () => {
    if (!title.trim() || submitting) return;
    setSubmitting(true);
    try {
      await suggestActivity(planId, {
        title: title.trim(),
        location: location.trim() || undefined,
        time_preference: timePref || undefined,
        note: note.trim() || undefined,
      });
      setSubmitted(true);
    } catch {
      // silent
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fork-modal-backdrop" onClick={onClose}>
      <div className="fork-modal" onClick={e => e.stopPropagation()} style={{ maxHeight: '80%', overflowY: 'auto' }}>
        <div className="fork-modal-handle" />
        <h3 className="fork-modal-title">Suggest an activity</h3>
        <p className="fork-modal-subtitle" style={{ marginBottom: 16 }}>
          Suggest something for {creatorName}'s plan. They'll decide whether to add it.
        </p>

        <label className="suggest-label">Activity name *</label>
        <input
          className="suggest-input"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="e.g., Fuunji Ramen"
        />

        <label className="suggest-label">Location <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(optional)</span></label>
        <input
          className="suggest-input"
          value={location}
          onChange={e => setLocation(e.target.value)}
          placeholder="e.g., Shinjuku"
        />

        <label className="suggest-label">When? <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(optional)</span></label>
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          {['Morning', 'Afternoon', 'Evening'].map(t => (
            <button
              key={t}
              onClick={() => setTimePref(timePref === t ? null : t)}
              style={{
                padding: '7px 14px',
                borderRadius: 16,
                border: `1px solid ${timePref === t ? 'var(--color-sage)' : 'var(--border)'}`,
                background: timePref === t ? 'rgba(78, 100, 112,0.15)' : 'var(--bg-card)',
                fontFamily: "var(--font-body)",
                fontSize: 12,
                color: 'var(--text-primary)',
                cursor: 'pointer',
              }}
            >
              {t}
            </button>
          ))}
        </div>

        <label className="suggest-label">Why this? <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(optional)</span></label>
        <textarea
          className="suggest-textarea"
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="e.g., Best tsukemen in Tokyo, worth the line..."
        />

        <button
          className="fork-modal-primary-btn"
          onClick={handleSubmit}
          disabled={!title.trim() || submitting}
          style={{ marginTop: 4 }}
        >
          {submitting ? 'Sending...' : 'Send suggestion'}
        </button>
        <button className="fork-modal-dismiss" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
};

export default SuggestModal;
